import {Link as RouterLink} from 'react-router-dom'
import {Trans, useTranslation} from 'react-i18next'
import ContactUsButton from '../components/ContactUsButton'
import './TermsPage.css'

interface TocEntry {
  readonly id: string
  readonly key: string
}

const TOC: readonly TocEntry[] = [
  {id: 'agreement', key: 'agreement'},
  {id: 'account', key: 'account'},
  {id: 'license', key: 'license'},
  {id: 'acceptable-use', key: 'acceptableUse'},
  {id: 'content', key: 'content'},
  {id: 'credits', key: 'credits'},
  {id: 'availability', key: 'availability'},
  {id: 'liability', key: 'liability'},
  {id: 'changes', key: 'changes'},
  {id: 'law', key: 'law'},
  {id: 'contact', key: 'contact'},
]

const PROSE_COMPONENTS = {
  refund: <RouterLink to="/refund" />,
  privacy: <RouterLink to="/privacy" />,
  account: <RouterLink to="/account" />,
  contact: <RouterLink to="/contact" />,
  strong: <strong />,
  em: <em />,
}

const pad2 = (n: number) => String(n).padStart(2, '0')

const TermsPage = () => {
  const {t} = useTranslation()

  return (
    <div className="terms-root">
      <div className="tm-grid" aria-hidden />

      <main className="terms-page">
        <header className="tm-hero">
          <span className="tm-eyebrow">
            <span className="tm-eyebrow-dot" />
            {t('terms.eyebrow')}
          </span>
          <h1 className="tm-title">
            {t('terms.titleLead')} <em>{t('terms.titleEm')}</em>
          </h1>
          <p className="tm-lede">{t('terms.lede')}</p>
          <div className="tm-meta">
            <span>{t('terms.metaUpdated')}</span>
            <span className="tm-meta-sep" />
            <span>{t('terms.metaReading')}</span>
            <span className="tm-meta-sep" />
            <span>{t('terms.metaGoverned')}</span>
          </div>
        </header>

        <div className="tm-layout">
          <aside className="tm-toc" aria-label={t('terms.tocLabel')}>
            <div className="tm-toc-label">{t('terms.tocLabel')}</div>
            {TOC.map((entry, i) => (
              <a key={entry.id} href={`#${entry.id}`}>
                <span className="tm-toc-num">{pad2(i + 1)}</span>
                <span>{t(`terms.toc.${entry.key}`)}</span>
              </a>
            ))}
          </aside>

          <article className="tm-prose">
            <section id="agreement">
              <h2 data-num="01">{t('terms.sections.agreement.title')}</h2>
              <p>
                <Trans i18nKey="terms.sections.agreement.p1" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="terms.sections.agreement.p2" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="terms.sections.agreement.p3" components={PROSE_COMPONENTS} />
              </p>
              <div className="tm-callout">
                <span className="tm-callout-icon" aria-hidden>
                  ★
                </span>
                <span>{t('terms.sections.agreement.callout')}</span>
              </div>
            </section>

            <section id="account">
              <h2 data-num="02">{t('terms.sections.account.title')}</h2>
              <p>
                <Trans i18nKey="terms.sections.account.p1" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="terms.sections.account.p2" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="terms.sections.account.p3" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="license">
              <h2 data-num="03">{t('terms.sections.license.title')}</h2>
              <p>
                <Trans i18nKey="terms.sections.license.p1" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="terms.sections.license.p2" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="acceptable-use">
              <h2 data-num="04">{t('terms.sections.acceptableUse.title')}</h2>
              <p>{t('terms.sections.acceptableUse.p1')}</p>
              <ul>
                <li>
                  <Trans i18nKey="terms.sections.acceptableUse.bullets.resale" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="terms.sections.acceptableUse.bullets.automation" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="terms.sections.acceptableUse.bullets.infringing" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="terms.sections.acceptableUse.bullets.impersonation" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="terms.sections.acceptableUse.bullets.security" components={PROSE_COMPONENTS} />
                </li>
              </ul>
              <p>
                <Trans i18nKey="terms.sections.acceptableUse.closing" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="content">
              <h2 data-num="05">{t('terms.sections.content.title')}</h2>
              <p>
                <Trans i18nKey="terms.sections.content.p1" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="terms.sections.content.p2" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="terms.sections.content.p3" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="terms.sections.content.p4" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="terms.sections.content.p5" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="credits">
              <h2 data-num="06">{t('terms.sections.credits.title')}</h2>
              <p>
                <Trans i18nKey="terms.sections.credits.p1" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="terms.sections.credits.p2" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="terms.sections.credits.p3" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="availability">
              <h2 data-num="07">{t('terms.sections.availability.title')}</h2>
              <p>
                <Trans i18nKey="terms.sections.availability.p1" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="liability">
              <h2 data-num="08">{t('terms.sections.liability.title')}</h2>
              <p>
                <Trans i18nKey="terms.sections.liability.p1" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="terms.sections.liability.p2" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="terms.sections.liability.p3" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="terms.sections.liability.p4" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="changes">
              <h2 data-num="09">{t('terms.sections.changes.title')}</h2>
              <p>
                <Trans i18nKey="terms.sections.changes.p1" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="law">
              <h2 data-num="10">{t('terms.sections.law.title')}</h2>
              <p>
                <Trans i18nKey="terms.sections.law.p1" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="terms.sections.law.p2" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="contact">
              <h2 data-num="11">{t('terms.sections.contact.title')}</h2>
              <p>{t('terms.sections.contact.p1')}</p>
              <div className="tm-address">
                <Trans i18nKey="terms.sections.contact.addressBody" components={{br: <br />, strong: <strong />}} />
              </div>
              <p>
                <Trans i18nKey="terms.sections.contact.emailLine" components={PROSE_COMPONENTS} />
              </p>
              <div className="tm-contact-cta">
                <ContactUsButton />
              </div>
            </section>
          </article>
        </div>
      </main>
    </div>
  )
}

export default TermsPage
